'use client'
import { Button, Container, Icon, Typography, TextField, Pagination, Drawer, Badge, Divider, Link } from '@shc/ui'
import { type ChangeEvent, type KeyboardEvent, type ComponentPropsWithoutRef, useEffect, useState } from 'react'
import { type Coordinates } from '@lib/utilities/location'
import { useInstantSearch, usePagination, useClearRefinements, useSearchBox, useCurrentRefinements, Configure } from 'react-instantsearch'
import SearchFacetMenu from '@components/search-facet-menu'
import FilteredPromo from '@components/filtered-promos'
import { doScroll } from '@hooks/useCustomScroll'
import { debounceQueryHook } from '@lib/search'
import Image from 'next/image'
import clsx from 'clsx'
import { usePathname } from 'next/navigation'
import ProviderSearchResult from '@components/search-result-doctor'
import { fadFacetConfigMap, searchInputDebounceMs } from '@lib/constants'
import SearchLocation from './search-location'
import { SearchNav } from "@components/search";

const queryHook = debounceQueryHook(searchInputDebounceMs);

export interface SearchHeroProps extends ComponentPropsWithoutRef<'div'> {
  title: string
  itemTitle?: string
  restrictedRegions: string[]
  features: string[]
  promos?: any
}
interface SearchResultsProps {
  nbHits: number;
  results: any;
  locationText?: string;
  itemTitle?: string
  site?: 'public' | 'providers';
  promos?: any
}

const SearchResults = ({
  nbHits,
  results,
  locationText,
  site = "public",
  promos
}: SearchResultsProps) => {
  const aroundLatLng = results?.aroundLatLng ?? results?._state?.aroundLatLng;
  const showNearSummary = Boolean(aroundLatLng && locationText);
  const defaultSummaryText = `${nbHits === 1 ? 'result' : 'results'} match your search`;

  return (
    <>
      <div className="flex flex-col gap-5 xl:gap-8" aria-live="polite">
        <p>
          <span className="font-bold">{nbHits}</span>{" "}
          {showNearSummary ? (
            <>
              results near{" "}
              <span className="font-bold">{locationText}</span>
            </>
          ) : (
            defaultSummaryText
          )}
        </p>
      </div>
      
      { results?.userData && promos && (
        <FilteredPromo userData={results.userData} promos={promos} />
      )}

      <Divider className='mt-8' />

      { results?.hits.map((item: any) => (
        <div key={item.id}>
          <ProviderSearchResult
            hit={item}
            site={site}
            aroundLatLng={aroundLatLng}
          />
        </div>
      ))}
    </>
  );
};

const SearchProviders = ({
  title,
  itemTitle,
  restrictedRegions,
  features,
  promos,
  onLocationConfigure,
  ...props
}: SearchHeroProps & { onLocationConfigure?: (coords: Coordinates | null, locationText?: string) => void }) => {

  const pathname = usePathname();
  const { results, status: instantSearchStatus } = useInstantSearch()
  const { nbHits, currentRefinement, nbPages, refine: refinePagination } = usePagination()
  const currentRefinements = useCurrentRefinements()

  //state
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false)

  // derived variables
  const facets = results.renderingContent?.facetOrdering?.facets?.order ?? []
  const showErrorState = instantSearchStatus === 'error'
  const showLoadState = instantSearchStatus === 'loading'
  const showNoResultsState = nbHits === 0 && !showLoadState && !showErrorState
  const showResults = !showErrorState && !showNoResultsState && !showLoadState
  const showPagination = showResults && nbPages > 1
  const showRefinements = currentRefinements.items?.length > 0
  const site = pathname.startsWith("/providers") ? 'providers' : 'public';

  const { refine } = useClearRefinements()
  const searchBox = useSearchBox({queryHook})
  const [queryValue, setQueryValue] = useState(searchBox.query)
  const [internalCoords, setInternalCoords] = useState<Coordinates | null>(null)
  const [locationText, setLocationText] = useState('')

  useEffect(() => {
    const aroundLatLng = results?.aroundLatLng ?? results?._state?.aroundLatLng
    const fallbackLocationText =
      aroundLatLng && !locationText
        ? new URL(globalThis.window.location.href).searchParams.get('l') ?? ''
        : ''

    if (fallbackLocationText) {
      setLocationText(fallbackLocationText)
    }
  }, [locationText, results])

  // Handlers
  const onPageChange = (page: number) => {
    refinePagination(page - 1)
    doScroll(0)
  }

  /* Handlers */
  const handleQueryKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      return false
    }
    return true
  }
  const handleQueryValueChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const q = event.target.value
    setQueryValue(q)
    searchBox.refine(q)
    if (q === '') {
      refine()
    }
  }

  const handleLocationChange = (coords: Coordinates | null, nextLocationText = '') => {
    setLocationText(nextLocationText)

    const url = new URL(globalThis.window.location.href)
    if (nextLocationText) {
      url.searchParams.set('l', nextLocationText)
    } else {
      url.searchParams.delete('l')
    }
    history.pushState(null, '', url.toString())

    if (onLocationConfigure) {
      onLocationConfigure(coords, nextLocationText)
      return
    }

    setInternalCoords(coords)
  }

  useEffect(() => {
    const syncLocationFromUrl = () => {
      const nextLocationText = new URL(globalThis.window.location.href).searchParams.get('l') ?? ''
      setLocationText(nextLocationText)
    }

    globalThis.window.addEventListener('popstate', syncLocationFromUrl)

    return () => {
      globalThis.window.removeEventListener('popstate', syncLocationFromUrl)
    }
  }, [])

  return (
    <div {...props} className={ site === 'providers' ? 'providers-search' : 'fad-search'}>
      <Drawer
        open={isFilterMenuOpen}
        onClose={() => setIsFilterMenuOpen(false)}
        aria-label="Search filters">
        <div
          className="flex flex-row items-start justify-between border-b pt-1 xl:pt-0 mb-5"
          data-testid="menu-drawer-header">
          <Typography variant="h5" className="w-full pb-5 text-left">
            Filters
          </Typography>
        </div>
        <SearchFacetMenu facets={facets} facetConfigMap={fadFacetConfigMap} restrictedRegions={restrictedRegions} disableSingleItemBehavior={false} site={site} />
      </Drawer>
      <form
        autoComplete="off"
        className={site === 'providers' ? "bg-primary" : "bg-primary-50"}
      >
        
        <Container className="grid grid-cols-12 col-span-12 col-start-1 xl:col-span-8 xl:col-start-1 py-6 md:py-8 lg:py-15 gap-4 md:gap-8">
          <div className="col-span-12 col-start-1 xl:col-span-8 xl:col-start-1">
            <Typography variant="h1" className={site === 'providers' ? "w-full text-white" : "w-full"}>
              {title}
            </Typography>
          </div>
          {/* search form fields */}
            <TextField
              type="search"
              value={queryValue}
              onChange={handleQueryValueChange}
              onKeyDown={handleQueryKeyDown}
              startDecorator={<Icon className="h-3.5 " icon="magnifying-glass" />}
              placeholder='Search by specialty, name or keyword'
              className='input-reset col-span-12 md:col-span-6 xl:col-span-6'
            />
            <SearchLocation onLocationChange={handleLocationChange} />
        </Container>
      </form>

      {/* If the parent wrapper didn't provide a location callback, use a local Configure to apply geolocation */}
      {!onLocationConfigure && (
        <Configure
          {...(internalCoords
            ? { aroundLatLng: `${internalCoords.lat},${internalCoords.lng}` }
            : { aroundLatLngViaIP: true })}
          aroundRadius="all"
          aroundPrecision={1000}
        />
      )}

      { site === 'providers' && (
        <SearchNav features={features} />
      )}

      <Container className='grid grid-cols-12 col-span-12 gap-6 text-gray-700 py-8 xl:py-15'>
        <div className='col-span-12'>
          <div className='flex flex-rows gap-x-10'>
            {/* LEFT COLUMN - FACETS*/}
            <div className='hidden xl:flex flex-col pr-4 w-[306px] min-w-[306px]'>
              <SearchFacetMenu facets={facets} facetConfigMap={fadFacetConfigMap} restrictedRegions={restrictedRegions} disableSingleItemBehavior={false} site={site} />
            </div>

            {/* RIGHT COLUMN - RESULTS */}
            <div className='flex flex-col flex-grow'>

              {/* Filter button - hide on xl */}
              {
                (showResults || showRefinements) && (
                  <div className='mb-5 md:mb-8 lg:mb-0 xl:hidden lg:pb-8'>
                    <Button
                      id="search-filter-button"
                      aria-label="Open search filters"
                      className={clsx("xl:hidden", showNoResultsState && "mb-5 md:mb-8")}
                      size="sm"
                      width="md"
                      startDecorator={<Icon icon="sliders-simple" />}
                      endDecorator={
                        currentRefinements.items.length > 0 && (
                          <Badge>
                            {currentRefinements.items.reduce(
                              (total, item) => total + item.refinements.length,
                              0,
                            )}
                          </Badge>
                        )
                      }
                      onClick={(e: React.MouseEvent<any>) => {
                        setIsFilterMenuOpen(true);
                      }}
                    >
                      Filters
                    </Button>
                  </div>
                )
              }

              {/* RESULTS ITEMS */}
              {showResults && (
                <SearchResults
                  nbHits={nbHits}
                  results={results}
                  locationText={locationText}
                  itemTitle={itemTitle}
                  site={site}
                  promos={promos}
                />
              )}

              <div className='flex flex-col flex-grow gap-4'>
                {/* ERROR */}
                {showErrorState && (
                  <p>
                    We&apos;re sorry. An error has occurred on the page you were attempting to view.
                    The following information has been reported and we will address this as soon as
                    possible.
                  </p>
                )}

                {/* NO RESULTS */}
                {showNoResultsState && (
                  <>
                    <Typography variant='h3' className=' xl:pt-0'>
                      No results matched your search term.
                    </Typography>
                    <Typography variant="body">
                      Adjust the filters, try a different search term or select another search category.
                    </Typography>
                    <Typography variant="body">
                      Need help?&nbsp;
                      <Link
                        href="/contact-us"
                        className='text-tertiary hover:text-linkhover'
                      >
                        Contact us
                      </Link>
                    </Typography>
                    <Image
                      src="/assets/images/svgs/plant-illustration.svg"
                      width={200}
                      height={200}
                      alt="Sharp logo"
                      className='mt-4 xl:mt-8'
                    />
                  </>
                )}
              </div>

              {/* PAGINATION */}
              {showPagination && (
                <div className='search-pagination pt-8 xl:pt-15'>
                  <Pagination
                    total={Math.min(nbPages * results.hitsPerPage, nbHits)}
                    pageSize={results.hitsPerPage}
                    current={currentRefinement + 1}
                    onChange={onPageChange}
                  />
                </div>
              )}
            </div>
          </div>

        </div>
      </Container>
    </div>
  )
}
export default SearchProviders